import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router";

/**
 * ESKİ (Türkçe) SORGU PARAMETRELERİNİ YENİ ADLARINA ÇEVİRMEK — bir sürümlük köprü.
 *
 * Kural `routing-and-navigation.md`'de: adresteki anahtar ve kodun sahip olduğu değer
 * İngilizce. Sohbet (`?panel=yeni`, `?arsiv=1`) ve rehber (`?sirket=`, `?birim=`) Türkçe
 * yazılmıştı; bu adresler yer imlerinde, bilet notlarında ve tarayıcı geçmişinde duruyor.
 * Bir sürüm boyunca eski adres AÇILIYOR ve adres çubuğu `replace` ile yeni adla yazılıyor —
 * geri tuşu eski adrese dönmüyor, paylaşılan bağlantı yeni biçimde paylaşılıyor.
 *
 * İKİ TÜR KURAL:
 * - anahtar değişimi: `{ from: "arsiv", to: "archived" }`;
 * - aynı anahtarda değer değişimi: `{ from: "panel", to: "panel", values: { yeni: "new" } }`.
 *
 * Yeni ad zaten adresteyse ESKİSİ atılıyor, yeni adın değeri kazanıyor: iki ad birden varsa
 * kişinin en son yazdığı ya da uygulamanın son ürettiği yenisidir.
 */
export interface LegacyParamRule {
  from: string;
  to: string;
  /** Kodun sahip olduğu değerlerin eski → yeni eşlemesi. Verilmeyen değer aynen taşınır. */
  values?: Readonly<Record<string, string>>;
}

/** Değişiklik yoksa `null` — çağıran böylece gereksiz bir gezinme yapmıyor. */
export function migrateLegacyParams(
  params: URLSearchParams,
  rules: readonly LegacyParamRule[],
): URLSearchParams | null {
  const next = new URLSearchParams(params);
  let changed = false;

  for (const rule of rules) {
    const raw = next.get(rule.from);
    if (raw === null) continue;
    const value = rule.values?.[raw] ?? raw;

    if (rule.from === rule.to) {
      if (value !== raw) {
        next.set(rule.to, value);
        changed = true;
      }
      continue;
    }

    next.delete(rule.from);
    if (!next.has(rule.to)) next.set(rule.to, value);
    changed = true;
  }

  return changed ? next : null;
}

/**
 * ESKİ BLOK AD ALANLARI — `talepler.inApproval` → `requests.inApproval` (31 Ekim 2026, UI/UX
 * denetimi K-A5).
 *
 * Bölüm sekmelerinde üst üste duran ekranların anahtarları bloğun adı altında (`SectionStack`,
 * `url-namespace-context.ts`); o adlar Türkçeydi ve adres dili kuralı yalnız yol segmentlerinde
 * ölçülüyordu. Kural `from`/`to` bir BLOK ADINI çeviriyor: o adın altındaki bütün anahtarlar
 * (`.q`, `.page`, `.panel`, `.record`, süzgeçler) yeni adın altına taşınıyor. Yeni ad zaten
 * adresteyse eski anahtar atılıyor — `migrateLegacyParams` ile aynı karar. Sıra korunuyor.
 */
export function migrateLegacyNamespaces(
  params: URLSearchParams,
  rules: readonly LegacyParamRule[],
): URLSearchParams | null {
  const next = new URLSearchParams();
  let changed = false;

  for (const [key, value] of params) {
    const dot = key.indexOf(".");
    const rule = dot > 0 ? rules.find((candidate) => candidate.from === key.slice(0, dot)) : undefined;
    if (!rule) {
      next.append(key, value);
      continue;
    }
    changed = true;
    const renamed = `${rule.to}${key.slice(dot)}`;
    if (!params.has(renamed)) next.append(renamed, value);
  }

  return changed ? next : null;
}

/**
 * Sayfanın OKUDUĞU parametreler çevrilmiş hâliyle, adres çubuğu da bir etkiyle yeni adla.
 *
 * Dönen `params` ilk çizimde bile yenidir: eski adresle gelen ekran bir kare boyunca
 * süzgeçsiz çizilip ardından süzülmüyor (arşiv düğmesi yanıp sönmüyor, fazladan liste isteği
 * gitmiyor). `rules` modül düzeyinde bir sabit olmalı — her çizimde yeni bir dizi etkiyi her
 * çizimde yeniden koşturur.
 */
export function useLegacySearchParams(
  rules: readonly LegacyParamRule[],
): ReturnType<typeof useSearchParams> {
  const [raw, setParams] = useSearchParams();
  const migrated = useMemo(() => migrateLegacyParams(raw, rules), [raw, rules]);

  useEffect(() => {
    if (migrated) setParams(migrated, { replace: true });
  }, [migrated, setParams]);

  return [migrated ?? raw, setParams];
}
